/**
 * Selection (R20).
 *
 * **Selection is client state.** It never enters the state hash — two players
 * having different units highlighted is not a divergence, and hashing it would
 * turn every click into a desync.
 */

import { Vector3 } from 'three'
import type { Ctx, Subsystem } from '../core/registry.js'
import { Entities, EF, NO_ENTITY } from '../sim/entities.js'
import { Renderer } from '../render/index.js'
import { AIR_ALTITUDE } from '../render/entities.js'
import * as fix from '../core/fixed.js'

/** Enough that any unit hit outranks any structure hit. Map is 128 cells square. */
const STRUCTURE_BIAS = 1e6
/** Pixels of pointer travel before a click becomes a drag. */
const DRAG_THRESHOLD = 5

export interface DragBox {
  active: boolean
  /** Client-space CSS pixels. */
  x0: number
  y0: number
  x1: number
  y1: number
}

export class Selection implements Subsystem {
  static id = 'selection'
  static deps = ['entities', 'render'] as const

  private ctx!: Ctx
  private entities!: Entities
  private renderer!: Renderer

  /** Selected entity ids, ascending. Ids, not slots, so a death invalidates. */
  private ids: number[] = []

  readonly drag: DragBox = { active: false, x0: 0, y0: 0, x1: 0, y1: 0 }

  /** The slot this client plays. Only its own units are selectable. */
  localSlot = 0

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.entities = ctx.get<Entities>('entities')
    this.renderer = ctx.get<Renderer>('render')
    // A dead unit leaves the selection immediately, so an order can never be
    // issued for it and then rejected at validation.
    ctx.on('actor:removed', (p) => this.remove((p as { id: number }).id))
  }

  /** Currently selected ids. A fresh array; callers may keep it. */
  get selected(): number[] {
    return this.ids.slice()
  }

  get count(): number {
    return this.ids.length
  }

  has(id: number): boolean {
    return this.ids.includes(id)
  }

  clear(): void {
    if (this.ids.length === 0) return
    this.ids = []
    this.ctx.emit('selection:changed', { ids: [] })
  }

  set(ids: number[]): void {
    // Ascending order so the selection panel and any derived command list are
    // stable between clicks that pick the same units in a different sweep.
    const next = ids.filter((id) => this.isSelectable(id)).sort((a, b) => a - b)
    this.ids = next
    this.ctx.emit('selection:changed', { ids: next.slice() })
  }

  add(ids: number[]): void {
    this.set([...this.ids, ...ids])
  }

  toggle(id: number): void {
    if (this.has(id)) this.set(this.ids.filter((x) => x !== id))
    else this.set([...this.ids, id])
  }

  remove(id: number): void {
    if (!this.has(id)) return
    this.set(this.ids.filter((x) => x !== id))
  }

  /** Drop ids whose entity has died. Cheap, and run before issuing orders. */
  prune(): void {
    const alive = this.ids.filter((id) => this.entities.isLive(id))
    if (alive.length !== this.ids.length) this.set(alive)
  }

  private isSelectable(id: number): boolean {
    const slot = this.entities.resolve(id)
    if (slot < 0) return false
    return this.entities.owner[slot] === this.localSlot
  }

  // --- picking -------------------------------------------------------------

  /**
   * The entity under a screen point, or NO_ENTITY. Coordinates come from the
   * renderer's `screenToWorld` — the single conversion pair — rather than being
   * derived here, because two independent derivations disagree and the symptom
   * is clicks landing on the wrong unit.
   */
  pick(clientX: number, clientY: number, includeEnemies = false): number {
    if (!this.renderer.screenToWorld(clientX, clientY, this.scratch)) return NO_ENTITY
    let best = NO_ENTITY
    let bestScore = Infinity
    for (let slot = 0; slot < this.entities.limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (!includeEnemies && this.entities.owner[slot] !== this.localSlot) continue
      const bounds = this.renderer.entityBounds(slot)
      if (!bounds || clientX < bounds.left || clientX > bounds.right ||
          clientY < bounds.top || clientY > bounds.bottom) continue
      const dx = (clientX - (bounds.left + bounds.right) / 2) / Math.max(1, bounds.right - bounds.left)
      const dy = (clientY - (bounds.top + bounds.bottom) / 2) / Math.max(1, bounds.bottom - bounds.top)
      const score = dx * dx + dy * dy + (this.entities.has(slot, EF.Structure) ? STRUCTURE_BIAS : 0)
      if (score < bestScore) {
        bestScore = score
        best = this.entities.idAt(slot)
      }
    }
    return best
  }

  /**
   * Own **mobile** units inside a screen rectangle. Structures and enemies are
   * excluded: a drag-box that swept up the enemy's base and your own refinery
   * would make the next right-click meaningless.
   */
  pickBox(x0: number, y0: number, x1: number, y1: number): number[] {
    const out: number[] = []
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (this.entities.owner[slot] !== this.localSlot) continue
      if (!this.entities.has(slot, EF.Mobile)) continue
      const wx = fix.toFloat(fix.raw(this.entities.x[slot]))
      const wz = fix.toFloat(fix.raw(this.entities.y[slot]))
      const lift = this.renderer.groundAt(wx, wz) + (this.entities.has(slot, EF.Air) ? AIR_ALTITUDE : 0)
      const screen = this.renderer.worldToScreen(this.scratch.set(wx, lift, wz))
      if (screen.x < Math.min(x0, x1) || screen.x > Math.max(x0, x1) ||
          screen.y < Math.min(y0, y1) || screen.y > Math.max(y0, y1)) continue
      out.push(this.entities.idAt(slot))
    }
    return out
  }

  /** Every visible own unit of the same kind. U14's double-click. */
  pickSameKind(id: number): number[] {
    const slot = this.entities.resolve(id)
    if (slot < 0) return []
    const kind = this.entities.kind[slot]
    const out: number[] = []
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== this.localSlot) continue
      if (this.entities.kind[s] !== kind) continue
      out.push(this.entities.idAt(s))
    }
    return out
  }

  // --- drag box ------------------------------------------------------------

  beginDrag(x: number, y: number): void {
    this.drag.active = false
    this.drag.x0 = x
    this.drag.y0 = y
    this.drag.x1 = x
    this.drag.y1 = y
  }

  updateDrag(x: number, y: number): boolean {
    this.drag.x1 = x
    this.drag.y1 = y
    if (!this.drag.active) {
      const dx = x - this.drag.x0
      const dy = y - this.drag.y0
      if (dx * dx + dy * dy > DRAG_THRESHOLD * DRAG_THRESHOLD) this.drag.active = true
    }
    return this.drag.active
  }

  endDrag(): DragBox | null {
    if (!this.drag.active) {
      this.drag.active = false
      return null
    }
    this.drag.active = false
    return { ...this.drag, active: true }
  }

  centroid(): { x: number; z: number } | null {
    if (this.ids.length === 0) return null
    let sx = 0
    let sz = 0
    let n = 0
    for (const id of this.ids) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      sx += fix.toFloat(fix.raw(this.entities.x[slot]))
      sz += fix.toFloat(fix.raw(this.entities.y[slot]))
      n++
    }
    return n === 0 ? null : { x: sx / n, z: sz / n }
  }

  private readonly scratch = new Vector3()
}
